import { useRef, useState } from "react"
import PropTypes from "prop-types"
import { OfflineBolt, UnfoldLessSharp, UnfoldMoreSharp } from "@mui/icons-material"
import { Popover, IconButton, ListSubheader, List, ListItemButton, ListItemText } from "@mui/material"
import Tooltip from "components/material/tooltip"
import "./styles.scss"
import { TOOLTIP_ENTER_DELAY_MS } from "helpers/constants"

export default function ExpansionMenu({ setExpansionMode, isExpanded, hasUnexpandedComposites, isLoading, disabled = false, disableExpanded = false }) {
  const expansionRef = useRef()
  const [menuAnchor, setMenuAnchor] = useState(null)

  const handleOpenMenu = () => {
    setMenuAnchor(expansionRef.current)
  }

  const handleCloseMenu = () => {
    setMenuAnchor(null)
  }

  const showExpandedView = () => {
    if (disableExpanded) {
      return
    }
    setExpansionMode(true)
    handleCloseMenu()
  }

  const showCollapsedView = () => {
    setExpansionMode(false)
    handleCloseMenu()
  }

  const expansionText = (isExpanded && hasUnexpandedComposites) ? "Warning: one or more composite securities were not expanded" : "Choose composite look-through..."

  let expansionIcon
  if (isExpanded && hasUnexpandedComposites) {
    expansionIcon = <>
      <OfflineBolt className="expansion-warning" />
      <UnfoldLessSharp />
    </>
  } else if (isExpanded) {
    expansionIcon = <UnfoldLessSharp />
  } else {
    expansionIcon = <UnfoldMoreSharp />
  }

  return <div className="components_expansion-menu">
    <Tooltip enterDelay={TOOLTIP_ENTER_DELAY_MS} title={expansionText}>
      <span>
        <IconButton
          ref={expansionRef}
          onClick={handleOpenMenu}
          className="expansion-button"
          size="large"
          loading={isLoading}
          disabled={disabled}
        >
          {expansionIcon}
        </IconButton>
      </span>
    </Tooltip>

    <Popover
      anchorEl={menuAnchor}
      open={!!menuAnchor}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      classes={{
        root: "component_expansion-menu-dropdown",
      }}
    >
      <List className="expansion-menu">
        <ListSubheader component="div">Choose Composite Look-Through</ListSubheader>
        <ListItemButton key="display-collapsed" className="expansion-menu-item" selected={!isExpanded} onClick={showCollapsedView}>
          <ListItemText
            primary={<>Collapsed &mdash; Holdings View <i>(Default)</i></>}
            secondary={<><i>View positions as held,</i>composite positions remain as atomic units (e.g., ETF, basket, or portfolio). Analytics are still computed using underlying constituents and may reflect composite effects.</>}
          />
        </ListItemButton>
        <ListItemButton key="display-expanded" className="expansion-menu-item" selected={isExpanded} onClick={showExpandedView} disabled={disableExpanded}>
          <ListItemText
            primary={<>Expanded &mdash; Exposure View</>}
            secondary={<>Analyze what you <i>effectively own.</i> Composite positions are broken down into their underlying securities (when data is available). Overlapping securities are netted, and can be de-aggregated with groups.</>}
          />
        </ListItemButton>
      </List>
    </Popover>
  </div>
}

ExpansionMenu.propTypes = {
  setExpansionMode: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  hasUnexpandedComposites: PropTypes.bool,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  disableExpanded: PropTypes.bool,
}
